import {
    hasBarValue,
    isResolvedBarValue,
    resolveBars,
    resolveFunnelColors,
} from './bars-form.helpers';
import { resolveSingleSeriesColor } from './palette-colors';
import type { ResolvedBar } from './bars-form.helpers';
import { MultilineLegend } from './multiline-chart';
import { EMPTY_RATE } from '../format-rate';
import { GRAY_200 } from '~styles/colors';
import { labelColorOn } from '~styles/contrast';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import type { KpiFormProps } from '../types';
import style from './bars-form.module.scss';

/** One step of the filled funnel. Tweens its fill and remainder percentages;
 * the fill's own width stays untweened so the CSS transition on `.funnel_fill`
 * (see the stylesheet) is what animates it. */
const FunnelStep = ({
    bar,
    color,
    allZero,
}: {
    bar: ResolvedBar;
    color: string;
    allZero: boolean;
}) => {
    const available = hasBarValue(bar);
    const remainder = available ? 100 - bar.pct : 0;
    const tweenedPct = useTweenedValue(available ? bar.pct : 0);
    const tweenedRemainder = useTweenedValue(remainder);

    if (!available) {
        return (
            <div className={style.funnel_track}>
                <span className={style.funnel_unavailable}>{EMPTY_RATE}</span>
            </div>
        );
    }
    if (allZero) {
        return <div className={style.funnel_track} />;
    }

    return (
        <div className={style.funnel_track}>
            <div
                className={style.funnel_fill}
                style={{
                    width: `${bar.pct}%`,
                    background: color,
                }}
            >
                {bar.pct >= 8 && (
                    <span
                        className={`${style.funnel_fill_label} ${
                            labelColorOn(color) === 'light'
                                ? style.funnel_fill_label_light
                                : style.funnel_fill_label_dark
                        }`}
                    >
                        {Math.round(tweenedPct)}%
                    </span>
                )}
            </div>
            {remainder >= 4 && (
                <div
                    className={style.funnel_remainder}
                    style={{ width: `${remainder}%` }}
                >
                    <span className={style.funnel_remainder_label}>
                        {Math.round(tweenedRemainder)}%
                    </span>
                </div>
            )}
        </div>
    );
};

/** Filled funnel variant (`barsStyle: 'filled'`): a full-width 40px track per
 * step with a legend below, instead of the label/track/value list. The fill
 * and its leftover complement each carry a centered percent, when wide enough. */
const FilledBars = ({
    steps,
    palette,
}: {
    steps: ReturnType<typeof resolveBars>;
    palette: KpiFormProps['palette'];
}) => {
    const colors = resolveFunnelColors(steps.length, palette);
    const availableSteps = steps.filter(isResolvedBarValue);
    const allZero =
        availableSteps.length > 0 &&
        availableSteps.every((step) => step.pct === 0);

    return (
        <div className={style.bars_form}>
            {steps.map((bar, index) => (
                <FunnelStep
                    key={`${bar.label}-${index}`}
                    bar={bar}
                    color={colors[index]}
                    allZero={allZero}
                />
            ))}
            <MultilineLegend
                unit="count"
                series={steps.map((bar, index) => ({
                    label: bar.label,
                    value: hasBarValue(bar) ? bar.value : null,
                    color: colors[index],
                }))}
            />
        </div>
    );
};

/** One row of the unfilled bar list. Tweens its value, percentage and base;
 * the track fill's width stays untweened so the CSS transition on `.bar_fill`
 * (see the stylesheet) is what animates it. */
const BarRow = ({
    bar,
    barBaseInHeader,
    fill,
}: {
    bar: ResolvedBar;
    barBaseInHeader?: boolean;
    fill: string;
}) => {
    const available = hasBarValue(bar);
    const tweenedValue = useTweenedValue(available ? bar.value : 0);
    const tweenedPct = useTweenedValue(available ? bar.pct : 0);
    const tweenedBase = useTweenedValue(
        available && bar.base != null ? bar.base : 0
    );

    return (
        <div className={style.bar_row}>
            <div className={style.bar_head}>
                <span className={style.bar_label}>{bar.label}</span>
                <span className={style.bar_value}>
                    {!available
                        ? EMPTY_RATE
                        : bar.rate != null
                        ? `${Math.round(tweenedValue).toLocaleString(
                              'en-US'
                          )} · ${Math.round(tweenedPct)}%${
                              !barBaseInHeader && bar.base != null
                                  ? ` (${Math.round(tweenedBase).toLocaleString(
                                        'en-US'
                                    )})`
                                  : ''
                          }`
                        : `${Math.round(tweenedValue).toLocaleString(
                              'en-US'
                          )} · ${Math.round(tweenedPct)}%`}
                </span>
            </div>
            <div className={style.bar_track} style={{ background: GRAY_200 }}>
                {available && (
                    <div
                        className={style.bar_fill}
                        style={{
                            width: `${bar.pct}%`,
                            background: fill,
                        }}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * Bars form: a horizontal bar list. Two modes, chosen by `resolveBars`:
 *
 * - Funnel mode (no step carries a `rate`): each bar is scaled to the largest
 *   value, readout is `value · NN%`.
 * - Rate mode (a step carries a `rate`): each bar's fill is its own absolute
 *   rate, readout is `value · NN% (base)`. When `barBaseInHeader` is set, the
 *   per-row base is dropped (each row reads just `value · NN%`) because the
 *   card title carries the shared base instead.
 *
 * A configured metric declared unavailable for the window (a null scalar)
 * renders its row as `N/A` with an empty track (no fill), distinct from a real
 * zero (which still fills, at 0%); its siblings render normally.
 */
export const BarsForm = ({
    data,
    barBaseInHeader,
    barsStyle,
    palette,
}: KpiFormProps) => {
    if (data.form !== 'bars') return null;
    const bars = resolveBars(data.steps);

    if (barsStyle === 'filled') {
        return <FilledBars steps={bars} palette={palette} />;
    }
    const fill = resolveSingleSeriesColor(palette);

    return (
        <div className={style.bars_form}>
            {bars.map((bar, index) => (
                <BarRow
                    key={`${bar.label}-${index}`}
                    bar={bar}
                    barBaseInHeader={barBaseInHeader}
                    fill={fill}
                />
            ))}
        </div>
    );
};
